import Cookie from 'js-cookie'

import tabtalk from '@shared/utils/tabtalk'
import { short } from '@shared/utils/uuid'

const RECONNECT_TIME_MIN = 30
const RECONNECT_TIME_MAX = 120

const DOMAIN = '.xiaoman.cn'
const SOCKET_STATE_ALIVE = 'socket-alive'
const SOCKET_UUID = 'socket-uuid'
const UUID = short()
const SOCKET_SETUP_CHECKING = 'socket-setup-checking'
const SOCKET_SETUP_CHECKING_FEEDBACK = 'socket-setup-checking-feedback'
const SOCKET_SELF_CHECKING_TIMESTAMP = 'socket-self-checking-timestamp'

const random = (min, max) => Math.floor(Math.random() * (max - min + 1) + min)

const calcRetryInterval = () =>
  random(RECONNECT_TIME_MIN, RECONNECT_TIME_MAX) * 1000

const RECONNECT_TIMES_LIMIT = 10
const SOCKET_STORAGE_KEY = '$$socket-message'

const STATE_OPEN = '1'
const STATE_CLOSE = '0'
export const setSocketAliveState = val => {
  Cookie.set(
    SOCKET_STATE_ALIVE,
    JSON.stringify({
      status: val,
      user_id: Cookie.get('userId') || window._globalData?.userInfo?.okkiUserId,
    }),
    { domain: DOMAIN }
  )
}

export const getSocketAliveState = () => {
  const stateStr = Cookie.get(SOCKET_STATE_ALIVE, { domain: DOMAIN })
  const state = stateStr
    ? JSON.parse(stateStr)
    : {
        status: STATE_CLOSE,
        userId: 0,
      }

  return state.user_id ===
    (Cookie.get('userId') || window._globalData?.userInfo?.okkiUserId)
    ? state.status
    : 0
}

const setSocketUUID = val => {
  Cookie.set(SOCKET_UUID, val, { domain: DOMAIN })
}

const getSocketUUID = () => Cookie.get(SOCKET_UUID, { domain: DOMAIN })

const setSelfCheckingTimestamp = val => {
  Cookie.set(SOCKET_SELF_CHECKING_TIMESTAMP, val, { domain: DOMAIN })
}

const getSelfCheckingTimestamp = () =>
  Cookie.get(SOCKET_SELF_CHECKING_TIMESTAMP, { domain: DOMAIN }) || '0'

const hasArrayBuffer = typeof ArrayBuffer === 'function'
const { toString } = Object.prototype

const isArrayBuffer = value =>
  hasArrayBuffer &&
  (value instanceof ArrayBuffer ||
    toString.call(value) === '[object ArrayBuffer]')

const arrayBufferToString = (u, f) => {
  const b = new Blob([u])
  const r = new FileReader()
  r.readAsText(b, 'utf-8')
  r.onload = () => {
    if (f) {
      f.call(null, r.result)
    }
  }
}

export default class MessageSocket {
  socket = null
  eventMap = {}
  SOCKET_URL = ''

  constructor(SOCKET_URL) {
    this.timerReconnect = null
    this.start()
    this.initEventBind()
    this.SOCKET_URL = SOCKET_URL
  }

  initEventBind() {
    tabtalk.$on(SOCKET_SETUP_CHECKING, () => {
      // 接受自检方法 如存在socket对象则将反馈广播出去
      if (!this.socket || getSocketUUID() !== UUID) {
        return
      }
      tabtalk.$broadcast(SOCKET_SETUP_CHECKING_FEEDBACK)
    })

    window.addEventListener('storage', e => {
      if (e.key === SOCKET_STORAGE_KEY && e.newValue !== null) {
        try {
          this.handleMessage(JSON.parse(e.newValue), false)
          // eslint-disable-next-line
        } catch (e) {}
      }
    })
  }

  // 初始化时广播自检方法
  setupSelfChecking() {
    if (Date.now() - Number(getSelfCheckingTimestamp()) < 3000) {
      return
    }
    setSelfCheckingTimestamp(Date.now())
    tabtalk.$emit(SOCKET_SETUP_CHECKING)

    this.selfCheckingTimer = setTimeout(() => {
      setSocketAliveState(STATE_CLOSE)
      this.setupSocket()
    }, 3000)

    tabtalk.$once(SOCKET_SETUP_CHECKING_FEEDBACK, () => {
      clearTimeout(this.selfCheckingTimer)
    })
  }

  setupSocket() {
    if (this.socket || getSocketAliveState() === STATE_OPEN) {
      return
    }

    import(/* webpackChunkName: "async-socket.io" */ 'socket.io-client').then(
      module => {
        const io = module.default
        this.socket = io(this.SOCKET_URL, {
          reconnectionAttempts: RECONNECT_TIMES_LIMIT,
          reconnectionDelay: calcRetryInterval(),
          transports: ['websocket'],
        })

        this.socket.on('message', res => {
          // 是否二进制数据
          ;(isArrayBuffer(res)
            ? new Promise(resolve => arrayBufferToString(res, resolve)).then(
                JSON.parse
              )
            : Promise.resolve(res)
          ).then(data => {
            this.handleMessage(data, true)

            localStorage.setItem(SOCKET_STORAGE_KEY, JSON.stringify(data))
            localStorage.removeItem(SOCKET_STORAGE_KEY)
          })
        })

        this.socket.on('disconnect', () => {
          if (this.socket) {
            this.socket.close()
          }
          this.socket = null
          setSocketAliveState(STATE_CLOSE)
          setSocketUUID('')
        })

        setSocketAliveState(STATE_OPEN)
        setSocketUUID(UUID)
        // 如果已经成功初始化socket 清空自检定时器
        clearTimeout(this.selfCheckingTimer)
      }
    )
  }

  setupReconnect() {
    this.timerReconnect = setTimeout(() => {
      this.setupSocket()
      this.setupReconnect()
    }, calcRetryInterval())
  }

  start() {
    this.setupSelfChecking()
    this.setupReconnect()
  }

  handleMessage(data, fromCurrentTab) {
    const { eventType } = data

    const triggerCallbacks = cbs =>
      cbs.forEach(func => {
        func(data, fromCurrentTab)
      })

    const eventHandlers = this.eventMap[eventType]
    if (eventHandlers) {
      triggerCallbacks(eventHandlers.callback)
    }

    const allHandlers = this.eventMap['*']
    if (allHandlers) {
      triggerCallbacks(allHandlers.callback)
    }
  }

  register(type, callback) {
    if (!this.eventMap[type]) {
      this.eventMap[type] = {
        callback: [],
      }
    }

    if (this.eventMap[type] && typeof callback === 'function') {
      this.eventMap[type].callback.push(callback)
    }
    return this
  }

  destroy() {
    clearTimeout(this.timerReconnect)
    clearTimeout(this.selfCheckingTimer)

    this.timerReconnect = null
    this.selfCheckingTimer = null

    if (this.socket) {
      this.socket.close()
      this.socket = null
      setSocketAliveState(STATE_CLOSE)
      setSocketUUID('')
    }
  }
}
